import { isPathSelected } from '../utils/path-utils';
import type { SearchResult } from '../utils/search-utils';
import { CascaderSearchResultItem } from './CascaderSearchResultItem';

type CascaderSearchResultsProps = {
  /** 検索結果 */
  searchResults: SearchResult[];
  /** 選択済みパス（複数選択モード用） */
  selectedPaths: string[][];
  /** 複数選択モードかどうか */
  multiple: boolean;
  /** 末端ノードのみ選択可能かどうか */
  onlyLeafSelectable: boolean;
  /** 検索結果選択時のコールバック */
  onSelectSearchResult: (result: SearchResult) => void;
  /** キーボードナビゲーション用のアクティブインデックス */
  activeIndex: number;
  /** フィールドのID（aria-activedescendant 用） */
  id?: string;
};

/**
 * 検索結果を表示するコンポーネント
 *
 * 検索モード時にフラットなリストで検索結果を表示します
 */
export const CascaderSearchResults = ({
  searchResults,
  selectedPaths,
  multiple,
  onlyLeafSelectable,
  onSelectSearchResult,
  activeIndex,
  id,
}: CascaderSearchResultsProps) => {
  return (
    <div
      role="listbox"
      id={id ? `${id}-search-listbox` : undefined}
      // 親 PopoverContent が flex container かつ boundary 配下で max-height 制限されている場合、
      // flex-1 + min-h-0 で外側内に自動収縮する。boundary なし時は max-h-[300px] が上限。
      className="max-h-[300px] min-h-0 min-w-[280px] flex-1 overflow-y-auto"
      onWheel={(e) => e.stopPropagation()}
    >
      {searchResults.map((result, idx) => {
        const isSelected = multiple && isPathSelected(result.path, selectedPaths);
        // 子要素を持つかどうか（中間階層かどうか）
        const hasChildren = Boolean(result.option.children && result.option.children.length > 0);
        // onlyLeafSelectable=true の場合、中間階層は選択不可
        const isSelectDisabled =
          Boolean(result.option.disabled) || (onlyLeafSelectable && hasChildren);
        // 複数選択モードでチェックボックスを表示するかどうか
        const showCheckbox = !onlyLeafSelectable || !hasChildren;

        return (
          <CascaderSearchResultItem
            key={result.path.join('-')}
            result={result}
            index={idx}
            isSelected={isSelected}
            isSelectDisabled={isSelectDisabled}
            isActive={idx === activeIndex}
            showCheckbox={showCheckbox}
            multiple={multiple}
            id={id}
            onSelect={() => !isSelectDisabled && onSelectSearchResult(result)}
          />
        );
      })}
    </div>
  );
};
